import type { FrameworkClass } from '../framework/framework.d';
import { FrameworkClasses } from '../framework/frameworkClasses';
import type { DriveUnitPortParameterName } from './driveUnitPortSelection';
import { DRIVE_UNIT_PORT_PARAMETERS, isUnsupportedDriveUnitPortRawValue } from './driveUnitPortSelection';
import { parseMechanismFollowerMotorsFromProjectCode } from './mechanismFollowerMotorUtils';
import {
  collectLastTopLevelVarAssignmentsByName,
  readConstructorKeywordValue,
  resolveConstructorDeclarationLineIndex,
  splitCodeLines,
} from './pythonSourceScanUtils';

export type DriveUnitPortInitEntry = {
  parameter: string;
  constructorArg: string;
};

/** Drive-unit port bindings use constructor keywords that include `port` (e.g. `port`, `motor_port`). */
export function isPortConstructorArg(constructorArg: string): boolean {
  return constructorArg.includes('port');
}

/**
 * Initialization rows that bind a drive-unit port (1–8) to a constructor keyword in snippets.
 * Derived from framework metadata (`constructor_arg` contains `port`), not a hardcoded device list.
 */
export function getDriveUnitPortInitEntries(frameworkClass: FrameworkClass): DriveUnitPortInitEntry[] {
  const entries: DriveUnitPortInitEntry[] = [];
  for (const item of frameworkClass.initialization ?? []) {
    const constructorArg = item.constructor_arg;
    if (typeof constructorArg !== 'string' || !isPortConstructorArg(constructorArg)) continue;
    const parameter = item.parameter;
    if (typeof parameter !== 'string' || parameter.length === 0) continue;
    entries.push({ parameter, constructorArg });
  }
  return entries;
}

export function parseDriveUnitPortValue(raw: unknown): number | null {
  if (isUnsupportedDriveUnitPortRawValue(raw)) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Resolves a drive-unit port from a constructor declaration line in program code.
 * Reads the framework-mapped constructor kwarg (e.g. `port` or `motor_port`), then falls back to the framework default.
 */
export function resolveDriveUnitPortAtDeclarationLine(
  projectCode: string,
  lineIndex0Based: number,
  frameworkClass: FrameworkClass,
  parameter: string
): number | null {
  const portEntry = getDriveUnitPortInitEntries(frameworkClass).find((entry) => entry.parameter === parameter);
  if (!portEntry) return null;

  const rawValue = readConstructorKeywordValue(projectCode, lineIndex0Based, portEntry.constructorArg);
  if (rawValue !== undefined) return parseDriveUnitPortValue(rawValue);

  const defaultInit = frameworkClass.initialization?.find((item) => item.parameter === parameter);
  return parseDriveUnitPortValue(defaultInit?.value);
}

/**
 * Resolves a drive-unit port for a named top-level variable, locating its constructor line in program code.
 */
export function resolveDriveUnitPortFromVariableDeclaration(
  projectCode: string,
  lineNumber: number,
  variableName: string,
  frameworkClass: FrameworkClass,
  parameter: string
): number | null {
  const lines = splitCodeLines(projectCode);
  const lineIndex = variableName
    ? resolveConstructorDeclarationLineIndex(lines, variableName, {
        constructorClassName: frameworkClass.name,
        fallbackLineIndex0Based: lineNumber,
      })
    : Number.isFinite(lineNumber)
    ? lineNumber
    : null;
  if (lineIndex === null) {
    const defaultInit = frameworkClass.initialization?.find((item) => item.parameter === parameter);
    return parseDriveUnitPortValue(defaultInit?.value);
  }

  return resolveDriveUnitPortAtDeclarationLine(projectCode, lineIndex, frameworkClass, parameter);
}

function frameworkClassNamesWithPortParameter(parameterName: DriveUnitPortParameterName): Set<string> {
  return new Set(
    FrameworkClasses.filter((frameworkClass) => getDriveUnitPortInitEntries(frameworkClass).some((entry) => entry.parameter === parameterName)).map(
      (frameworkClass) => frameworkClass.name
    )
  );
}

/**
 * Python variable names for electronics occupying a drive-unit port in program code.
 * Covers motors, sensors, mechanisms, Bit Adapters, and mechanism follower motors (`motor_port`).
 */
export function collectElectronicsVariableNamesOnPort(programCode: string, port: number, parameterName: DriveUnitPortParameterName): string[] {
  if (!programCode.trim()) return [];

  const names = new Set<string>();
  const lines = splitCodeLines(programCode);

  for (const { variableName, className, lineIndex } of collectLastTopLevelVarAssignmentsByName(lines, {
    allowedClassNames: frameworkClassNamesWithPortParameter(parameterName),
  })) {
    const frameworkClass = FrameworkClasses.find((candidate) => candidate.name === className);
    if (!frameworkClass) continue;

    const driveUnitPort = resolveDriveUnitPortAtDeclarationLine(programCode, lineIndex, frameworkClass, parameterName);
    if (driveUnitPort === port) names.add(variableName);
  }

  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR) {
    for (const follower of parseMechanismFollowerMotorsFromProjectCode(programCode)) {
      if (follower.port === port) names.add(follower.mechanismVariableName);
    }
  }

  return [...names];
}
